import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, GoogleLogo, Exam, PencilLine, Books, Trophy, ChartLineUp, Sparkle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { STATECRAFT_LOGO } from "@/lib/brand";

const HERO = "https://images.pexels.com/photos/36817688/pexels-photo-36817688.jpeg";
const STUDY_IMG = "https://images.pexels.com/photos/33940741/pexels-photo-33940741.jpeg";

export default function Landing() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();

    const handleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/dashboard";
        // prompt=select_account forces Google to show the account picker even when
        // only one account is signed in (avoids silent auto-login).
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&prompt=select_account`;
    };

    // "Sign in with a different account" — Google's Logout endpoint rejects arbitrary
    // continue URLs (400 error), so we open the logout page in a small popup. The user
    // waits for it to say "You signed out", closes it, and then we launch the normal
    // sign-in — Google will now show the account chooser.
    const handleSwitchAccount = () => {
        const popup = window.open(
            "https://accounts.google.com/Logout",
            "google_logout",
            "width=520,height=620,menubar=no,toolbar=no,location=no,status=no"
        );
        if (!popup) {
            // Popup blocked — fall back to full navigation to Google logout; user can hit Back.
            window.location.href = "https://accounts.google.com/Logout";
            return;
        }
        // Poll for the popup being closed by the user; then trigger normal sign-in.
        const timer = setInterval(() => {
            if (popup.closed) {
                clearInterval(timer);
                handleLogin();
            }
        }, 500);
    };

    React.useEffect(() => {
        if (!loading && user) navigate("/dashboard", { replace: true });
    }, [loading, user, navigate]);

    return (
        <div className="min-h-screen bg-white text-slate-900">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-[#DBE4FF]">
                <div className="max-w-7xl mx-auto px-6 py-4 relative flex items-center justify-center">
                    <div className="flex flex-col items-center gap-1">
                        <img src={STATECRAFT_LOGO} alt="Statecraft IAS Academy" className="h-20 sm:h-24 w-auto object-contain" />
                        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                            Chintana Portal · <span className="text-[#1E3A8A] font-semibold">KPSC · KEA Prep</span>
                        </div>
                    </div>
                    <div className="absolute right-6 top-1/2 -translate-y-1/2">
                        <button data-testid="header-signin-btn" onClick={handleLogin} className="btn-ghost">
                            <GoogleLogo size={16} weight="bold" /> Sign in
                        </button>
                    </div>
                </div>
            </header>

            {/* Hero — Vidhana Soudha as cloudy backdrop */}
            <section className="relative isolate overflow-hidden">
                {/* Background image */}
                <div className="absolute inset-0 -z-20">
                    <img
                        src={HERO}
                        alt="Vidhana Soudha, Bengaluru"
                        className="w-full h-full object-cover"
                        style={{ filter: "blur(1px) saturate(1.05)", objectPosition: "center 20%" }}
                    />
                </div>
                {/* Soft "cloudy" overlay — white haze so text reads on top */}
                <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white/50 via-white/35 to-white/65" />
                <div className="absolute inset-0 -z-10 bg-gradient-to-r from-white/40 via-transparent to-white/40" />

                <div className="max-w-7xl mx-auto px-6 pt-20 pb-28 relative">
                    <div className="max-w-7xl mx-auto mb-6">
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#1E3A8A]/25 bg-white/80 backdrop-blur-sm px-3 py-1.5 text-xs font-medium text-[#1E3A8A]">
                            <Sparkle size={14} weight="fill" className="text-gold" />
                            An initiative of Statecraft IAS Academy · Crafting the path to success
                        </div>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-3xl mx-auto text-center"
                    >
                        <h1 className="font-heading font-black tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-slate-900" style={{ textShadow: "0 2px 18px rgba(255,255,255,0.85)" }}>
                            Crack <span className="text-gradient-navy">KPSC & KEA</span><br />
                            with clarity, not chaos.
                        </h1>
                        <p className="mt-6 text-lg text-slate-800 max-w-2xl mx-auto leading-relaxed bg-white/60 backdrop-blur-sm rounded-2xl px-5 py-3 border border-white/70">
                            Timed MCQ practice for KAS, PSI, FDA/SDA and Karnataka CET. Write mains-style answers, get a
                            <span className="text-[#1E3A8A] font-semibold"> Claude-powered rubric evaluation</span> and climb the leaderboard.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                            <button data-testid="get-started-btn" onClick={handleLogin} className="btn-primary group">
                                <GoogleLogo size={20} weight="bold" />
                                Start with Google
                                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                            </button>
                            <a href="#features" className="btn-ghost bg-white/70 backdrop-blur-sm">See what's inside</a>
                        </div>
                        <button
                            data-testid="switch-account-btn"
                            onClick={handleSwitchAccount}
                            className="mt-3 text-xs text-slate-700 hover:text-[#1E3A8A] underline underline-offset-4 decoration-slate-400 hover:decoration-[#1E3A8A]"
                            title="Opens Google sign-out in a small window; close it and you'll be signed in again with a fresh account chooser."
                        >
                            Use a different Google account
                        </button>
                        <div className="mt-10 text-[10px] uppercase tracking-[0.35em] text-slate-600">
                            Vidhana Soudha · Bengaluru · <span className="text-[#1E3A8A] font-semibold">Rooted in Karnataka</span>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-24 border-t border-[#BFD3FF] bg-slate-50/50">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="max-w-2xl mb-14">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#1E3A8A] mb-3">What's inside</div>
                        <h2 className="font-heading font-bold text-4xl sm:text-5xl leading-tight text-slate-900">A quiet corner of the internet, built to help you clear the exam.</h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {[
                            { Icon: Exam, title: "Timed MCQ Tests", desc: "KAS, PSI, FDA/SDA, KEA CET. Subject-wise. Timer, navigator, mark-for-review. Instant analytics." },
                            { Icon: PencilLine, title: "Mains Evaluation", desc: "Write, submit, and get a structured rubric — introduction, body, conclusion, accuracy, language, and marks/10." },
                            { Icon: Books, title: "PYQ Archive", desc: "Previous year questions from KPSC & KEA, searchable by year, subject and exam." },
                            { Icon: ChartLineUp, title: "Progress Analytics", desc: "Readiness score, subject strength map and score trend charts to focus your revision." },
                            { Icon: Trophy, title: "Leaderboard", desc: "Compete with fellow aspirants. Weekly and all-time rankings by score & accuracy." },
                            { Icon: Sparkle, title: "Kannada + English", desc: "Bilingual questions where it matters. Rooted in Karnataka's syllabus and culture." },
                        ].map(({ Icon, title, desc }) => (
                            <div key={title} className="bento-card">
                                <Icon size={28} weight="duotone" className="text-[#1E3A8A] mb-4" />
                                <div className="font-heading font-semibold text-lg mb-1 text-slate-900">{title}</div>
                                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 border-t border-[#BFD3FF]">
                <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <h3 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-tight text-slate-900">
                            The exam rewards <span className="text-gradient-navy">focused</span> practice.
                        </h3>
                        <p className="mt-4 text-slate-600 text-lg max-w-lg">
                            Not endless scrolling. Chintana strips away the noise so you can spend your hours on what actually moves the needle.
                        </p>
                        <button data-testid="cta-signin-btn" onClick={handleLogin} className="btn-primary mt-8">
                            <GoogleLogo size={20} weight="bold" /> Sign in with Google · Start free
                        </button>
                    </div>
                    <div className="relative rounded-3xl overflow-hidden aspect-[4/3] border border-[#BFD3FF]">
                        <img src={STUDY_IMG} alt="Focused study" className="w-full h-full object-cover" />
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-[#BFD3FF] bg-white">
                <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <img src={STATECRAFT_LOGO} alt="Statecraft" className="h-10 w-auto" />
                        <div className="text-xs text-slate-500">© Statecraft IAS Academy · Crafting the path to success</div>
                    </div>
                    <div className="text-xs text-slate-500">Chintana · Purpose-built for KPSC & KEA aspirants</div>
                </div>
            </footer>
        </div>
    );
}
